import {
  AbsenceListDto,
  AbsenceType,
  AppointmentType,
  QueryFactory,
  ResourceType,
} from '@lmt/shared-types';
import {
  AbsenceCalendarAppointment,
  AbsenceProps,
} from '@lmt/unicorn-shared/app/components/absences/AbsenceCalendarAppointment';
import { AbsencePopup } from '@lmt/unicorn-shared/app/components/absences/popup/AbsencePopup';
import { AppPopper } from '@lmt/unicorn-shared/app/components/common/ui-kit/tooltip/AppPopper';
import {
  addDays,
  endOfMonth,
  isSameDay,
  isWithinInterval,
  startOfMonth,
  startOfWeek,
  endOfWeek,
  isSameMonth,
  isSameYear,
  addMilliseconds,
  startOfDay,
} from 'date-fns';
import { bindHover, bindPopper } from 'material-ui-popup-state';
import { usePopupState } from 'material-ui-popup-state/hooks';
import React, { FC, useCallback, useMemo, useRef, useState } from 'react';

import {
  AppointmentClickEvent,
  AppointmentDblClickEvent,
  AppointmentFormOpeningEvent,
  DXS,
  useDxScheduler,
} from '@lmt/unicorn-shared/app/common/devexpress/Scheduler/dx-scheduler';
import HolidayIcon from '@lmt/unicorn-shared/app/icons/holiday.svg?react';
import clsx from 'clsx';
import { useEventCallback } from '@mui/material';
import { useResourceDictionary } from '../../api/custom-hooks';
import { localFormat } from '../../helpers/date-helper';
import styles from './AbsenceMonthCalendar.module.scss';
import { CalendarDateCellDropHandler } from '../calendar/devextreme/components/DragAndDropPatient';
import { useGlobalForms } from '../global-forms/useGlobalForms';
import { CalendarCellDropTarget } from '../calendar/devextreme/common';
import { AppointmentItem } from '../calendar/devextreme/resource-view/ResourceViewAppointment';
import { useFirstDayOfWeek } from '../calendar/common';
import { useClaims } from '../../features/auth/operations';

interface Props {
  currentDate: Date;
}

const editingSettings = {
  allowDragging: false,
  allowResizing: false,
  allowDeleting: false,
  allowAdding: false,
  allowEditingTimeZones: false,
  allowUpdating: false,
};

export const AbsenceMonthCalendar: FC<Props> = (props) => {
  const { currentDate } = props;
  useDxScheduler();

  const claims = useClaims();
  const firstDayOfWeek = useFirstDayOfWeek();
  const forms = useGlobalForms();
  const [popupAbsence, setPopupAbsence] = useState<
    | {
        absence: AbsenceListDto[];
        currentDate: Date | null /* null in case of multi-day absence */;
      }
    | undefined
  >(undefined);

  const absenceQuery =
    QueryFactory.AppointmentEventsQuery.useSearchAppointmentsQuery({
      from: startOfWeek(startOfMonth(currentDate), {
        weekStartsOn: firstDayOfWeek,
      }),
      to: endOfWeek(endOfMonth(currentDate), { weekStartsOn: firstDayOfWeek }),
      eventTypes: [AppointmentType.Absence],
    });

  const absences = (absenceQuery?.data as AbsenceListDto[]) ?? [];
  const resourceQuery = useResourceDictionary(true);
  const absencesAsData = useMemo(() => {
    const appointments = absences.filter(
      (e) => e.absenceType === AbsenceType.ResourceUnavailability,
    );
    const lastCalendarDay = endOfWeek(endOfMonth(currentDate), {
      weekStartsOn: firstDayOfWeek,
    });
    const result = appointments.reduce(
      (accum, appointment) => {
        const startDateString = appointment.start.toISOString();
        const resources = resourceQuery.data ?? {};
        const typeToKey = (type?: ResourceType) =>
          type === null || type === undefined
            ? ''
            : type === ResourceType.Doctors
              ? 'doc'
              : 'places+special';
        appointment.resourceIds.forEach((resourceId) => {
          const key = startDateString + typeToKey(resources[resourceId]?.type);
          if (!accum[key])
            accum[key] = {
              startDate: appointment.start,
              endDate:
                appointment.end > lastCalendarDay
                  ? lastCalendarDay
                  : appointment.end,
              id: appointment.id,
              data: [{ ...appointment, resourceIds: [] }],
            };
          accum[key].data[0].resourceIds.push(resourceId);
        });
        return accum;
      },
      {} as Record<
        string,
        {
          startDate: Date;
          endDate: Date;
          id: number | undefined;
          data: AbsenceListDto[];
        }
      >,
    );
    return Object.values(result);
  }, [absences, resourceQuery.data]);

  const onResourceUnavailabilityMouseEnter = useEventCallback(
    (target: HTMLElement, absence: AbsenceListDto[] | undefined) => {
      setPopupAbsence(
        absence && absence.length
          ? { absence: absence, currentDate: null }
          : undefined,
      );
    },
  );
  const onHolidayMouseEnter = useEventCallback(
    (
      target: HTMLElement,
      absence: AbsenceListDto[] | undefined,
      date: Date | null,
    ) => {
      setPopupAbsence(
        absence && absence.length
          ? { absence: absence, currentDate: date }
          : undefined,
      );
    },
  );

  const popupState = usePopupState({
    variant: 'popper',
    popupId: 'absence-month-calendar',
  });
  const bindHoverRef = bindHover(popupState);

  // the pop-up offset for AbsenceType.Holiday is required for fixing
  // bug #54419: popup with holidays closed when trying to hover in Month view in Absences and holidays
  //
  // if we don't use useMemo, due to the animation of the pop-up appearance
  // when the cursor moves, the pop-up often reappears
  const modifiers = useMemo(() => {
    return popupAbsence?.absence.some(
      (absence) => absence.absenceType === AbsenceType.Holiday,
    )
      ? [
          {
            name: 'offset',
            options: {
              offset: [0, -90],
              // when 90 is set, the popup appears exactly under the name of the holiday,
              // and it works for any number of absences in the cell
              // in general it is 3/4 of the height of the cell in Month View (AbsenceMonthCalendar.module.scss - .dx-scheduler-cell-sizes-vertical)
            },
          },
        ]
      : [];
  }, [popupAbsence?.absence]);

  const renderDateCell = useCallback((cellData: { date: Date }) => {
    const cellDate = cellData.date;
    return <div>{localFormat(cellDate, 'E')}</div>;
  }, []);

  const onDataCellClick = useEventCallback(
    (
      holidayId: number | undefined,
      cell: Omit<CalendarCellDropTarget, 'type' | 'resource'>,
    ) => {
      if (!claims.permissions.manageAbsences) {
        return;
      }

      if (holidayId) {
        forms.openAbsenceEditing(holidayId);
        return;
      }
      forms.openAbsenceCreating({
        start: cell.startDate,
        end: cell.endDate ? cell.endDate : addDays(cell.startDate, 1),
      });
      return;
    },
  );

  const renderDataCell = useCallback(
    (cellProps: {
      startDate: Date;
      endDate: Date;
      allDay?: boolean;
      groups: { resources: number };
    }) => {
      const holidays =
        absences
          ?.filter(
            (x) =>
              x.absenceType === AbsenceType.Holiday &&
              isWithinInterval(cellProps.startDate, {
                start: startOfDay(x.start),
                end: x.end,
              }) &&
              cellProps.startDate.getTime() !== x.end.getTime(),
          )
          .sort((a, b) =>
            a.title.toLowerCase() > b.title.toLowerCase()
              ? 1
              : b.title.toLowerCase() > a.title.toLowerCase()
                ? -1
                : 0,
          ) ?? [];
      const holiday: AbsenceListDto | undefined = holidays
        ? (holidays.find((x) => isSameDay(cellProps.startDate, x.start)) ??
          holidays[0])
        : undefined;

      const isFirstDayOfHoliday = holiday
        ? isSameDay(cellProps.startDate, holiday.start)
        : false;

      return (
        <div
          is-today={`${isSameDay(cellProps.startDate, new Date())}`}
          onDoubleClick={() => {
            onDataCellClick(holiday?.id, {
              startDate: cellProps.startDate,
              endDate: cellProps.endDate,
            });
          }}
          onMouseOver={(e) => {
            onHolidayMouseEnter(
              e.currentTarget,
              holidays,
              holidays.length > 1 ? cellProps.startDate : null,
            );
            bindHoverRef.onMouseOver(e);
          }}
          onMouseLeave={(e) => {
            bindHoverRef.onMouseLeave(e);
          }}
          className={clsx(
            styles.dataCell,
            holidays.length > 0 && styles.holidayContent,
          )}
        >
          <CalendarDateCellDropHandler
            startDate={cellProps.startDate}
            endDate={cellProps.endDate}
          >
            <div className={styles.dataCellHeader}>
              <div className={styles.dayOfMonthText}>
                {localFormat(cellProps.startDate, 'd')}
              </div>
              {holiday && isFirstDayOfHoliday && (
                <div className={clsx(styles.header)}>
                  <HolidayIcon className={styles.icon} />
                  <span className={styles.title}>{holiday.title}</span>
                </div>
              )}
            </div>
          </CalendarDateCellDropHandler>
        </div>
      );
    },
    [absences, props.currentDate],
  );

  const onAppointmentFormOpening = useCallback(
    (e: AppointmentFormOpeningEvent) => {
      e.cancel = true;
    },
    [],
  );

  const onAppointmentClick = useCallback((e: AppointmentClickEvent) => {
    e.cancel = true;
  }, []);

  const onAppointmentDoubleClick = useCallback(
    (e: AppointmentDblClickEvent) => {
      e.cancel = true;
      const holidayData =
        e.appointmentData as AppointmentItem['data']['appointmentData'];
      if (holidayData.isAnonymized || holidayData.absenceType) {
        return;
      }
      const holidayId = holidayData?.id as number;
      if (holidayId) {
        forms.openAbsenceEditing(holidayId);
      }
    },
    [],
  );

  const AbsenceAppointmentComponent: FC<AbsenceProps> = useCallback(
    (innerProps) => {
      return (
        <AbsenceCalendarAppointment
          onAbsenceMouseEnter={onResourceUnavailabilityMouseEnter}
          onMouseOver={(e) => {
            bindHoverRef.onMouseOver(e);
          }}
          onMouseLeave={(e) => {
            bindHoverRef.onMouseLeave(e);
          }}
          isOtherMonth={isOtherMonth(
            innerProps.data.appointmentData.startDate as Date,
            innerProps.data.appointmentData.endDate as Date,
            currentDate,
          )}
          {...innerProps}
        />
      );
    },
    [absences],
  );

  return (
    <div className={clsx(styles.absenceContainer)}>
      <div>
        <DXS.Scheduler
          key={absencesAsData.length}
          showCurrentTimeIndicator={false}
          firstDayOfWeek={firstDayOfWeek}
          dataSource={absencesAsData as any}
          currentDate={currentDate}
          currentView={'month'}
          maxAppointmentsPerCell={'unlimited'}
          dateCellRender={renderDateCell}
          dataCellRender={renderDataCell}
          onAppointmentDblClick={onAppointmentDoubleClick}
          onAppointmentClick={onAppointmentClick}
          onAppointmentFormOpening={onAppointmentFormOpening}
          appointmentComponent={AbsenceAppointmentComponent}
          editing={editingSettings}
        />

        {popupAbsence && (
          <AppPopper
            {...bindPopper(popupState)}
            delay={300}
            modifiers={modifiers}
          >
            <AbsencePopup
              date={popupAbsence.currentDate}
              events={popupAbsence.absence}
              handleClose={popupState.close}
            />
          </AppPopper>
        )}
      </div>
    </div>
  );
};

function isOtherMonth(start: Date, end: Date, current: Date) {
  if (isSameMonth(start, current) && isSameYear(start, current)) {
    return false;
  }
  if (
    isSameMonth(addMilliseconds(end, -1), current) &&
    isSameYear(addMilliseconds(end, -1), current)
  ) {
    return false;
  }
  if (isWithinInterval(current, { start: start, end: end })) {
    return false;
  }
  return true;
}
